'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

interface Event {
  id: string;
  title: string;
  start_date: string | null;
  start_time: string | null;
  roles: Array<{
    id: string;
    role_name: string;
    role_type: string | null;
  }>;
}

interface AdminQuickCastDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dancerId: string;
  dancerName: string;
  events?: Event[];
  onAssign: (eventId: string, roleId: string) => Promise<void>;
}

export function AdminQuickCastDialog({
  open,
  onOpenChange,
  dancerId: _dancerId,
  dancerName,
  events = [],
  onAssign,
}: AdminQuickCastDialogProps) {
  const { t } = useTranslation();
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>(
    undefined,
  );
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(
    undefined,
  );
  const [isAssigning, setIsAssigning] = useState(false);

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  const handleAssign = async () => {
    if (!selectedEventId || !selectedRoleId) {
      toast.error(t('admin:quickCast.selectBoth'));
      return;
    }

    setIsAssigning(true);
    try {
      await onAssign(selectedEventId, selectedRoleId);
      toast.success(t('admin:quickCast.assignSuccess', { name: dancerName }));
      onOpenChange(false);
      // Reset selections
      setSelectedEventId(undefined);
      setSelectedRoleId(undefined);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('admin:quickCast.assignError'),
      );
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[500px]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            <Trans i18nKey="admin:quickCast.title" />
          </DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="admin:quickCast.description"
              values={{ name: dancerName }}
              components={{ strong: <span className="font-semibold" /> }}
            />
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto pr-2">
          {/* Event Selection */}
          <div className="space-y-2">
            <label htmlFor="quick-cast-event" className="text-sm font-medium">
              <Trans i18nKey="admin:quickCast.eventLabel" />
            </label>
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger id="quick-cast-event">
                <SelectValue placeholder={t('admin:quickCast.selectEvent')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>
                    <Trans i18nKey="admin:quickCast.upcomingEvents" />
                  </SelectLabel>
                  {events.length === 0 ? (
                    <SelectItem value="none" disabled>
                      <Trans i18nKey="admin:quickCast.noUpcomingEvents" />
                    </SelectItem>
                  ) : (
                    events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title} ({event.start_date ?? 'TBD'})
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <label htmlFor="quick-cast-role" className="text-sm font-medium">
              <Trans i18nKey="admin:quickCast.roleLabel" />
            </label>
            <Select
              value={selectedRoleId}
              onValueChange={setSelectedRoleId}
              disabled={!selectedEventId}
            >
              <SelectTrigger id="quick-cast-role">
                <SelectValue placeholder={t('admin:quickCast.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>
                    <Trans i18nKey="admin:quickCast.availableRoles" />
                  </SelectLabel>
                  {!selectedEvent || selectedEvent.roles.length === 0 ? (
                    <SelectItem value="none" disabled>
                      <Trans i18nKey="admin:quickCast.noRolesAvailable" />
                    </SelectItem>
                  ) : (
                    selectedEvent.roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.role_name} ({role.role_type})
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAssigning}
          >
            <Trans i18nKey="admin:common.buttons.cancel" />
          </Button>
          <Button onClick={handleAssign} disabled={isAssigning}>
            {isAssigning ? (
              <Trans i18nKey="admin:quickCast.submitting" />
            ) : (
              <Trans i18nKey="admin:quickCast.submit" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
